import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, Grid, Badge, Dropdown } from "antd";
import IntlMessage from "../util-components/IntlMessage";
import Icon from "../util-components/Icon";
import navigationConfig from "configs/NavigationConfig";
import { connect } from "react-redux";
import { NAV_TYPE_SIDE } from "constants/ThemeConstant";
import utils from "utils";
import { onMobileNavToggle } from "redux/actions/Theme";
import { THEME_KEYS } from "utils/themeDisplay";

const { SubMenu } = Menu;
const { useBreakpoint } = Grid;

const setLocale = (isLocaleOn, localeKey) =>
	isLocaleOn ? <IntlMessage id={localeKey} /> : localeKey.toString();

const MenuItem = ({
	title,
	icon,
	path,
	badgeCount,
	localization,
	closeMobileNav,
}) => {
	const translatedTitle = setLocale(localization, title);
	const content = (
		<>
			{icon && <Icon type={icon} className="ant-menu-item-icon" />}
			<span>{translatedTitle}</span>
			{badgeCount !== undefined && badgeCount !== null ? (
				<Badge count={badgeCount} overflowCount={999} />
			) : null}
		</>
	);

	return path ? (
		<Link to={path} onClick={closeMobileNav}>
			{content}
		</Link>
	) : (
		content
	);
};

const getSideNavMenuItem = (navItem, extras = {}) =>
	navItem.map((nav) => {
		const badgeCount = extras.badges?.[nav.key];
		const hasSubmenu = nav.submenu && nav.submenu.length > 0;
		const iconElement = nav.icon ? React.createElement(nav.icon) : null;

		if (hasSubmenu && nav.icon) {
			return {
				key: nav.key,
				label: (
					<MenuItem
						title={nav.title}
						badgeCount={badgeCount}
						localization={extras.localization}
					/>
				),
				icon: iconElement,
				children: getSideNavMenuItem(nav.submenu, extras),
				permissao: nav.permissao,
			};
		}

		return {
			key: nav.key,
			label: (
				<MenuItem
					title={nav.title}
					badgeCount={badgeCount}
					localization={extras.localization}
					{...(hasSubmenu ? {} : { path: nav.path, icon: nav.icon })}
				/>
			),
			...(hasSubmenu
				? { children: getSideNavMenuItem(nav.submenu, extras) }
				: {}),
			permissao: nav.permissao,
		};
	});

const findParentKeys = (config, targetKey, parents = []) => {
	for (const item of config) {
		if (item.key === targetKey) {
			return parents;
		}
		if (item.submenu && item.submenu.length > 0) {
			const found = findParentKeys(item.submenu, targetKey, [
				...parents,
				item.key,
			]);
			if (found !== null) return found;
		}
	}
	return null;
};

const findItemPath = (config, targetKey) => {
	for (const item of config) {
		if (item.key === targetKey) {
			return item.path;
		}
		if (item.submenu && item.submenu.length > 0) {
			const found = findItemPath(item.submenu, targetKey);
			if (found) return found;
		}
	}
	return null;
};

const findNavNode = (config, targetKey) => {
	for (const item of config) {
		if (item.key === targetKey) return item;
		if (item.submenu?.length) {
			const found = findNavNode(item.submenu, targetKey);
			if (found) return found;
		}
	}
	return null;
};

const SideNavContent = (props) => {
	const {
		routeInfo,
		hideGroupTitle,
		navCollapsed = false,
		currentTheme = "light",
		localization = true,
		onMobileNavToggle,
		notificacoesMenu = {},
		listaPermissoes,
	} = props;

	const isMobile = !utils.getBreakPoint(useBreakpoint()).includes("lg");
	const closeMobileNav = () => {
		if (isMobile) onMobileNavToggle(false);
	};

	const verificarPermissoes = useCallback(
		(permissoes) => {
			if (permissoes === undefined || permissoes === null) return true;
			if (!Array.isArray(permissoes)) permissoes = [permissoes];
			for (const p of permissoes) {
				if (!p || listaPermissoes[p]) return true;
			}
			return false;
		},
		[listaPermissoes],
	);

	function filterByPermissao(items) {
		return items
			.map((item) => {
				if (!verificarPermissoes(item.permissao)) return null;
				if (item.children?.length) {
					const ch = filterByPermissao(item.children);
					if (!ch.length) return null;
					return { ...item, children: ch };
				}
				return item;
			})
			.filter(Boolean);
	}

	const [openKeys, setOpenKeys] = useState([]);

	const badges = useMemo(() => {
		const b = {};
		const walk = (items) => {
			for (const n of items) {
				if (n.indexNotificacoes != null) {
					b[n.key] = notificacoesMenu[n.indexNotificacoes];
				}
				if (n.submenu?.length) walk(n.submenu);
			}
		};
		walk(navigationConfig);
		return b;
	}, [notificacoesMenu]);

	useEffect(() => {
		if (navCollapsed) {
			setOpenKeys([]);
		} else if (routeInfo?.key) {
			const parentKeys = findParentKeys(navigationConfig, routeInfo.key);
			if (parentKeys?.length) setOpenKeys(parentKeys);
		}
	}, [routeInfo?.key, navCollapsed]);

	const handleOpenChange = (keys) => {
		if (navCollapsed) {
			setOpenKeys([]);
			return;
		}
		const closedKeys = openKeys.filter((key) => !keys.includes(key));
		if (closedKeys.length > 0) {
			setOpenKeys(keys);
			return;
		}
		let keysToKeepOpen = [];
		if (routeInfo?.key) {
			const parentKeys = findParentKeys(navigationConfig, routeInfo.key);
			if (parentKeys?.length) keysToKeepOpen = parentKeys;
		}
		setOpenKeys([...new Set([...keys, ...keysToKeepOpen])]);
	};

	const handleMenuClick = ({ key }) => {
		const parentKeys = findParentKeys(navigationConfig, key);
		if (parentKeys?.length) {
			setOpenKeys([...new Set([...openKeys, ...parentKeys])]);
		}
	};

	const menuItems = useMemo(
		() =>
			getSideNavMenuItem(navigationConfig, {
				badges,
				localization,
			}),
		[badges, localization],
	);

	let newItems = [];
	for (const items of menuItems) {
		if (items.children && items.children.length > 0) {
			const children = filterByPermissao(items.children);
			if (children.length > 0) {
				if (navCollapsed) {
					const dropdownItems = children.map((child) => {
						const path = findItemPath(navigationConfig, child.key);
						const node = findNavNode(navigationConfig, child.key);
						const titleKey = node?.title || child.key;
						const childIcon = node?.icon;
						return {
							key: child.key,
							label: (
								<Link
									to={path || "#"}
									onClick={closeMobileNav}
									style={{
										display: "inline-flex",
										alignItems: "center",
										gap: "0.5rem",
									}}
								>
									{childIcon ? (
										<Icon
											type={childIcon}
											className="ant-menu-item-icon"
										/>
									) : null}
									<span>{setLocale(localization, titleKey)}</span>
								</Link>
							),
						};
					});

					newItems.push({
						key: items.key,
						label: (
							<Dropdown
								overlay={
									<Menu
										mode="vertical"
										selectedKeys={[routeInfo?.key]}
										style={{ border: "none", minWidth: 220 }}
										items={dropdownItems}
									/>
								}
								trigger={["hover"]}
								placement="rightTop"
								getPopupContainer={() => document.body}
								overlayClassName="side-nav-group-dropdown-overlay"
							>
								<div
									style={{
										cursor: "pointer",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										width: "100%",
										height: "100%",
									}}
								>
									{items.icon}
								</div>
							</Dropdown>
						),
						icon: null,
					});
				} else {
					newItems.push({ ...items, children });
				}
			}
		} else if (verificarPermissoes(items.permissao)) {
			newItems.push(items);
		}
	}

	const menuAntTheme =
		currentTheme === THEME_KEYS.LIGHT ? "light" : "dark";

	return (
		<Menu
			mode="inline"
			theme={menuAntTheme}
			style={{
				height: "100%",
				borderRight: 0,
			}}
			selectedKeys={[routeInfo?.key]}
			openKeys={navCollapsed ? [] : openKeys}
			onOpenChange={handleOpenChange}
			onClick={handleMenuClick}
			triggerSubMenuAction={navCollapsed ? "hover" : "click"}
			className={`side-nav-menu ${hideGroupTitle ? "hide-group-title" : ""}`}
			items={newItems}
		/>
	);
};

const TopNavContent = (props) => {
	const {
		topNavColor,
		localization,
		listaPermissoes = {},
		notificacoesMenu = {},
	} = props;

	const verificarPermissoesSubmenu = (listaItens) => {
		for (const item of listaItens) {
			let ok = verificarPermissoes(item.permissao);
			if (item.submenu?.length > 0) {
				ok = ok && verificarPermissoesSubmenu(item.submenu);
			}
			if (ok) return true;
		}
		return false;
	};

	const verificarPermissoes = (permissoes) => {
		if (permissoes === undefined || permissoes === null) return true;
		if (!Array.isArray(permissoes)) permissoes = [permissoes];
		for (const p of permissoes) {
			if (!p || listaPermissoes[p]) return true;
		}
		return false;
	};

	return (
		<Menu
			mode="horizontal"
			triggerSubMenuAction="hover"
			disabledOverflow
			style={{ backgroundColor: topNavColor }}
		>
			{navigationConfig.map((menu) => {
				if (menu.submenu.length > 0) {
					if (
						!verificarPermissoes(menu.permissao) ||
						!verificarPermissoesSubmenu(menu.submenu)
					) {
						return null;
					}
					return (
						<SubMenu
							key={menu.key}
							popupClassName="top-nav-menu"
							title={
								<span>
									{menu.icon ? <Icon type={menu.icon} /> : null}
									<span>{setLocale(localization, menu.title)}</span>
								</span>
							}
						>
							{menu.submenu.map((subMenuFirst) => {
								if (subMenuFirst.submenu.length > 0) {
									if (
										!verificarPermissoes(subMenuFirst.permissao) ||
										!verificarPermissoesSubmenu(subMenuFirst.submenu)
									) {
										return null;
									}
									return (
										<SubMenu
											key={subMenuFirst.key}
											icon={
												subMenuFirst.icon ? (
													<Icon type={subMenuFirst.icon} />
												) : null
											}
											title={setLocale(
												localization,
												subMenuFirst.title,
											)}
										>
											{subMenuFirst.submenu.map((subMenuSecond) =>
												verificarPermissoes(subMenuSecond.permissao) ? (
													<Menu.Item key={subMenuSecond.key}>
														<span>
															{setLocale(
																localization,
																subMenuSecond.title,
															)}
														</span>
														<Link to={subMenuSecond.path} />
													</Menu.Item>
												) : null,
											)}
										</SubMenu>
									);
								}
								if (!verificarPermissoes(subMenuFirst.permissao)) return null;
								return (
									<Menu.Item key={subMenuFirst.key}>
										{subMenuFirst.icon ? (
											<Icon type={subMenuFirst.icon} />
										) : null}
										<Badge
											count={
												notificacoesMenu[
													subMenuFirst.indexNotificacoes
												]
											}
											offset={[18]}
										>
											<span>
												{setLocale(localization, subMenuFirst.title)}
											</span>
										</Badge>
										<Link to={subMenuFirst.path} />
									</Menu.Item>
								);
							})}
						</SubMenu>
					);
				}
				if (!verificarPermissoes(menu.permissao)) return null;
				return (
					<Menu.Item key={menu.key}>
						{menu.icon ? <Icon type={menu.icon} /> : null}
						<Badge
							count={notificacoesMenu[menu.indexNotificacoes]}
							offset={[18]}
						>
							<span>{setLocale(localization, menu?.title)}</span>
						</Badge>
						{menu.path ? <Link to={menu.path} /> : null}
					</Menu.Item>
				);
			})}
		</Menu>
	);
};

const MenuContent = (props) => {
	return props.type === NAV_TYPE_SIDE ? (
		<SideNavContent {...props} />
	) : (
		<TopNavContent {...props} />
	);
};

const mapStateToProps = ({ theme, menu, permissoes }) => {
	const { topNavColor, currentTheme } = theme;
	return {
		topNavColor,
		currentTheme,
		notificacoesMenu: menu,
		listaPermissoes: permissoes,
	};
};

export default connect(mapStateToProps, { onMobileNavToggle })(MenuContent);
